"use client";

import type { ReactNode } from "react";

import type { TenantIdentity } from "@/config/tenants";
import { tenantPresentationConfigurations } from "@/config/tenant-presentation";
import { AuthBrand } from "@/features/auth/components/auth-brand";
import { authCopy } from "@/features/auth/content/auth-copy";
import { useAppLocale } from "@/features/localization/app-locale-provider";

interface AuthShellBrand {
  headingLines: readonly string[];
  descriptionLead: string;
  descriptionEmphasis: string;
  footnote: string;
}

interface AuthShellProps {
  brand?: AuthShellBrand;
  children: ReactNode;
  headingClassName?: string;
  tenant: TenantIdentity;
}

const DEFAULT_HEADING_CLASS_NAME =
  "text-4xl leading-[1.08] font-extrabold tracking-tight sm:text-5xl xl:text-[54px] 2xl:text-[58px] xl:[@media(max-height:800px)]:text-[48px]";

export function AuthShell({ brand, children, headingClassName, tenant }: Readonly<AuthShellProps>) {
  const { locale, setLocale } = useAppLocale();
  const copy = authCopy[locale];
  const brandCopy = brand ?? copy.brand;
  const centersBrandContent = tenantPresentationConfigurations[tenant.code]?.authContentAlignment === "center";

  return (
    <div className="grid min-h-screen overflow-x-hidden bg-background font-sans text-foreground xl:grid-cols-[43fr_57fr]">
      <header className="tenant-structure px-6 py-7 sm:px-10 sm:py-9 xl:min-h-svh xl:px-12 xl:py-10 2xl:px-16">
        <div className="mx-auto flex h-full w-full max-w-2xl flex-col">
          <div className="flex items-start justify-end gap-4">
            <nav
              aria-label={locale === "fr" ? "Langue" : "Language"}
              className="flex shrink-0 items-center gap-2 text-sm font-semibold text-[var(--tenant-structure-foreground,var(--primary-foreground))]"
              suppressHydrationWarning
            >
              <button
                aria-pressed={locale === "en"}
                className={locale === "en" ? "opacity-100" : "opacity-60 hover:opacity-100"}
                onClick={() => setLocale("en")}
                suppressHydrationWarning
                type="button"
              >
                {copy.language.en}
              </button>
              <span aria-hidden="true" className="opacity-60">/</span>
              <button
                aria-pressed={locale === "fr"}
                className={locale === "fr" ? "opacity-100" : "opacity-60 hover:opacity-100"}
                onClick={() => setLocale("fr")}
                suppressHydrationWarning
                type="button"
              >
                {copy.language.fr}
              </button>
            </nav>
          </div>
          <div className="flex flex-1 items-center pt-12 pb-3 sm:pt-16 sm:pb-6 xl:py-12 xl:pb-16 xl:[@media(max-height:800px)]:py-8 xl:[@media(max-height:800px)]:pb-12">
            <div className={`max-w-lg ${centersBrandContent ? "mx-auto text-center" : ""}`}>
              <div className={`mb-8 ${centersBrandContent ? "flex justify-center" : ""}`}>
                <AuthBrand tenant={tenant} />
              </div>
              <h1 className={headingClassName ?? DEFAULT_HEADING_CLASS_NAME}>
                {brandCopy.headingLines.map((line) => (
                  <span className="block" key={line}>{line}</span>
                ))}
              </h1>
              <p className={`tenant-structure-muted mt-6 max-w-md text-base leading-relaxed xl:mt-7 xl:text-lg xl:[@media(max-height:800px)]:mt-5 ${centersBrandContent ? "mx-auto" : ""}`}>
                {brandCopy.descriptionLead}
                <strong className="font-semibold text-[var(--primary)]">
                  {brandCopy.descriptionEmphasis}
                </strong>
                .
              </p>
              <p className={`tenant-structure-muted mt-4 max-w-md text-sm leading-relaxed ${centersBrandContent ? "mx-auto" : ""}`}>
                {brandCopy.footnote}
              </p>
            </div>
          </div>
        </div>
      </header>
      <main className="flex min-w-0 items-center justify-center bg-linear-to-br from-background via-muted/20 to-background px-5 py-6 sm:px-8 xl:min-h-screen xl:px-12 xl:py-8 2xl:px-16 xl:[@media(max-height:800px)]:py-5">
        <div className="w-full max-w-[480px] 2xl:max-w-[500px]">{children}</div>
      </main>
    </div>
  );
}
